import React, { useEffect, useState } from "react";
import { AccountLink } from "../components/resellers/AccountLink";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { fmtDate, fmtNum } from "../lib/format";
import { FileMinus, Search, X, Eye, Loader2, Trash2 } from "lucide-react";
import { Checkbox } from "../components/ui/checkbox";
import { useConfirm } from "../components/ConfirmDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import { REASON_LABEL } from "../components/CreditNotePdf";

/**
 * Phase 101fk (Feb 2026) — Liste des notes de crédit (Avoirs).
 *
 * Design aligné sur `Invoices.jsx` (même colonnes, mêmes filtres, palette
 * rouge doux pour distinguer les avoirs des factures).
 */

const STATUS_META = {
  BROUILLON: { label: "Brouillon", color: "bg-[#F0EBD8] text-[#6B6B4E] border-[#D4C97A]" },
  ENVOYEE: { label: "Envoyée", color: "bg-[#E8DAD2] text-[#8B3A1F] border-[#C08363]" },
  EXECUTEE: { label: "Exécutée", color: "bg-[#DAE5D7] text-[#3E5C34] border-[#7FA070]" },
  ANNULEE: { label: "Annulée", color: "bg-[#EEE] text-[#6B6E6B] border-[#C5C6C1] line-through" },
};


export default function CreditNotes() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showCancelled, setShowCancelled] = useState(false);
  const confirm = useConfirm();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/credit-notes");
      setItems(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur chargement");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const purge = async (nc) => {
    const ok = await confirm({
      title: `Supprimer le brouillon ${nc.reference} ?`,
      description: "Suppression définitive (brouillon jamais émis).",
      confirmLabel: "Supprimer",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/credit-notes/${nc.id}/purge`);
      toast.success(`Brouillon ${nc.reference} supprimé`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const filtered = items.filter((nc) => {
    if (statusFilter !== "all" && nc.status !== statusFilter) return false;
    if (statusFilter === "all" && !showCancelled && nc.status === "ANNULEE") return false;
    if (q) {
      const needle = q.toLowerCase();
      const hay = `${nc.reference} ${nc.debtor_name} ${nc.reason} ${nc.reason_detail || ""}`.toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    return true;
  });

  return (
    <div className="space-y-6" data-testid="credit-notes-page">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8B3A1F]">
            Documents comptables
          </p>
          <h1 className="font-heading text-3xl text-[#1A1C1A] font-semibold flex items-center gap-2 mt-1">
            <FileMinus className="w-7 h-7 text-[#8B3A1F]" /> Notes de crédit (Avoirs)
          </h1>
          <p className="text-sm text-[#575A57] mt-1">
            {items.length} avoir(s) enregistré(s). Pour créer un avoir, ouvre une facture puis clique « Créer un avoir ».
          </p>
        </div>
      </div>

      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#575A57]" />
              <Input
                placeholder="Rechercher n°, client, motif…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-9 pr-9"
                data-testid="cn-search"
              />
              {q && (
                <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#575A57] hover:text-[#1A1C1A]">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40" data-testid="cn-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="BROUILLON">Brouillons</SelectItem>
                <SelectItem value="ENVOYEE">Envoyées</SelectItem>
                <SelectItem value="EXECUTEE">Exécutées</SelectItem>
                <SelectItem value="ANNULEE">Annulées</SelectItem>
              </SelectContent>
            </Select>
            <label className="flex items-center gap-2 text-sm text-[#575A57] cursor-pointer">
              <Checkbox checked={showCancelled} onCheckedChange={(v) => setShowCancelled(!!v)} data-testid="cn-show-cancelled" />
              Afficher les annulés
            </label>
          </div>

          <div className="border border-[#E5E5E0] rounded overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#FAFAF7]">
                  <TableHead className="w-32">Référence</TableHead>
                  <TableHead className="w-28">Émis le</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Motif</TableHead>
                  <TableHead className="w-32 text-right">Total TTC</TableHead>
                  <TableHead className="w-28 text-center">Statut</TableHead>
                  <TableHead className="w-24"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow><TableCell colSpan={7} className="text-center py-6 text-[#575A57]">
                    <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Chargement…
                  </TableCell></TableRow>
                )}
                {!loading && filtered.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center py-6 text-[#575A57]">
                    Aucun avoir. Crée un depuis la fiche facture (bouton « Créer un avoir »).
                  </TableCell></TableRow>
                )}
                {!loading && filtered.map((nc) => {
                  const meta = STATUS_META[nc.status] || STATUS_META.BROUILLON;
                  return (
                    <TableRow key={nc.id} data-testid={`cn-row-${nc.id}`}>
                      <TableCell className="font-mono font-semibold">{nc.reference}</TableCell>
                      <TableCell className="text-xs">{fmtDate(nc.issue_date)}</TableCell>
                      <TableCell><AccountLink resellerId={nc.reseller_id} distributorId={nc.distributor_id} compact testid={`cn-account-${nc.id}`}>{nc.debtor_name}</AccountLink></TableCell>
                      <TableCell className="text-xs">
                        {REASON_LABEL[nc.reason] || nc.reason}
                        {nc.reason_detail && <div className="text-[10px] text-[#575A57]">{nc.reason_detail}</div>}
                      </TableCell>
                      <TableCell className="text-right font-mono text-[#8B3A1F] font-semibold">
                        − {fmtNum(nc.total_ttc)} CHF
                        {((nc.applied_total || 0) > 0 || (nc.refunded_amount || 0) > 0) && (
                          <div className="text-[10px] font-normal text-[#3E5C34]" data-testid={`cn-row-settled-${nc.id}`}>
                            {(nc.applied_total || 0) > 0 && `imputé ${fmtNum(nc.applied_total)}`}
                            {(nc.applied_total || 0) > 0 && (nc.refunded_amount || 0) > 0 && " · "}
                            {(nc.refunded_amount || 0) > 0 && `remboursé ${fmtNum(nc.refunded_amount)}`}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className={`${meta.color} text-[10px] font-mono`}>{meta.label}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex">
                          <Link to={`/notes-de-credit/${nc.id}`} data-testid={`cn-open-${nc.id}`}>
                            <Button size="sm" variant="ghost"><Eye className="w-4 h-4" /></Button>
                          </Link>
                          {nc.status === "BROUILLON" && (
                            <Button size="sm" variant="ghost" onClick={() => purge(nc)} title="Supprimer le brouillon" data-testid={`cn-purge-${nc.id}`}>
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
